import React from 'react';
import type { ComplianceState } from '../../receptionist-settings/types';
import { RCPT_SETTINGS_LOCKED_PROTECTIONS } from '../../receptionist-settings/mockData';
import { Icon } from '../Icon/Icon';
import {
  SettingsFieldRow,
  SettingsPageHeader,
  SettingsSectionHeader,
  SettingsToggle,
} from './SettingsShared';

export interface ComplianceSettingsPageProps {
  state: ComplianceState;
  setState: React.Dispatch<React.SetStateAction<ComplianceState>>;
  initial: ComplianceState;
}

export function ComplianceSettingsPage({ state, setState, initial }: ComplianceSettingsPageProps) {
  const upd = (key: keyof ComplianceState, value: ComplianceState[keyof ComplianceState]) =>
    setState((s) => ({ ...s, [key]: value }));
  const dirty = (key: keyof ComplianceState) => state[key] !== initial[key];

  return (
    <>
      <SettingsPageHeader
        eyebrow="Behavior · compliance"
        title="Compliance"
        italic="boundaries"
        description="Some are hard limits we enforce for every practice; the rest are dials your office can set based on state law, practice norms, and risk tolerance."
      />

      <section className="set-fade-up set-d-1 mb-[26px]">
        <div className="set-card">
          <SettingsSectionHeader
            num="01"
            title="Hard limits that can't be turned off"
            sub="Enforced for every patient on every channel. Categories where a wrong answer could harm someone or expose the practice to liability."
            railColor="var(--set-brass-deep)"
          />
          {RCPT_SETTINGS_LOCKED_PROTECTIONS.map((item, i) => (
            <div
              key={item.id}
              className={[
                'grid grid-cols-[auto_1fr_auto] items-start gap-3.5 px-[22px] py-4',
                i === RCPT_SETTINGS_LOCKED_PROTECTIONS.length - 1 ? '' : 'border-b border-[var(--cream-2)]',
              ].join(' ')}
            >
              <div
                className="mt-px inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] border"
                style={{
                  background: 'var(--set-brass-tint)',
                  color: 'var(--set-brass-deep)',
                  borderColor: 'rgba(168, 132, 92, 0.20)',
                }}
              >
                <Icon kind="lock" size={14} stroke={1.6} />
              </div>
              <div className="min-w-0">
                <div className="mb-1 text-[14px] font-semibold tracking-[-0.005em] text-[var(--ink-0)]">
                  {item.text}
                </div>
                <div className="text-[12.5px] leading-[1.55] tracking-[-0.005em] text-[var(--ink-2)]">
                  {item.note}
                </div>
              </div>
              <span
                className="mt-[5px] inline-flex shrink-0 items-center gap-1.5 rounded-[5px] border px-[9px] py-[3px] font-[var(--font-mono)] text-[10px] font-bold uppercase tracking-[0.06em]"
                style={{
                  background: 'var(--set-brass-tint)',
                  color: 'var(--set-brass-deep)',
                  borderColor: 'rgba(168, 132, 92, 0.20)',
                }}
              >
                Enforced
              </span>
            </div>
          ))}
        </div>
      </section>

      <section className="set-fade-up set-d-2 mb-[26px]">
        <div className="set-card">
          <SettingsSectionHeader
            num="02"
            title="HIPAA and patient information sharing"
            sub="How Elva handles protected health information across calls, texts, and emails."
          />
          <SettingsFieldRow
            title="Verify identity before sharing chart info"
            description="Before Elva shares appointment details, balances, or chart info, she confirms identity with name + one verifier (DOB, phone, or zip)."
            isDirty={dirty('verifyIdentityBeforeSharing')}
          >
            <SettingsToggle
              value={state.verifyIdentityBeforeSharing}
              onChange={(v) => upd('verifyIdentityBeforeSharing', v)}
            />
          </SettingsFieldRow>
          <SettingsFieldRow
            title="Include patient names in outbound voicemails"
            description="Including the name in a voicemail can be a HIPAA leak if someone else hears it. Off means voicemails say 'calling from Bayside Dental about an appointment.'"
            isDirty={dirty('patientNameInVoicemails')}
          >
            <SettingsToggle
              value={state.patientNameInVoicemails}
              onChange={(v) => upd('patientNameInVoicemails', v)}
            />
          </SettingsFieldRow>
          <SettingsFieldRow
            title="Share patient information with anyone other than the patient"
            description="Spouse/parent calling about a patient — Elva will only share if the patient has previously authorized this contact in writing."
            isDirty={dirty('shareWithThirdParties')}
            last
          >
            <SettingsToggle
              value={state.shareWithThirdParties}
              onChange={(v) => upd('shareWithThirdParties', v)}
            />
          </SettingsFieldRow>
        </div>
      </section>

      <section className="set-fade-up set-d-3 mb-[26px]">
        <div className="set-card">
          <SettingsSectionHeader
            num="03"
            title="Money conversations on autopilot"
            sub="The two most contentious financial topics for dental practices. Defaults are conservative."
          />
          <SettingsFieldRow
            title="Quote specific fees without insurance verification"
            description="When off, Elva quotes a range and offers to verify the patient's specific responsibility. On lets Elva quote the standard fee directly."
            isDirty={dirty('quoteUnverifiedFees')}
          >
            <SettingsToggle value={state.quoteUnverifiedFees} onChange={(v) => upd('quoteUnverifiedFees', v)} />
          </SettingsFieldRow>
          <SettingsFieldRow
            title="Discuss outstanding balances over the phone"
            description="When off, Elva says 'there's a balance — Sarah at the front desk can walk through it at your visit.' On lets her share the amount."
            isDirty={dirty('discussBalancesOnPhone')}
            last
          >
            <SettingsToggle
              value={state.discussBalancesOnPhone}
              onChange={(v) => upd('discussBalancesOnPhone', v)}
            />
          </SettingsFieldRow>
        </div>
      </section>

      <section className="set-fade-up set-d-4 mb-[26px]">
        <div className="set-card">
          <SettingsSectionHeader
            num="04"
            title="Call recording and AI disclosure"
            sub="Two-party consent is required in 11 states (CA, FL, IL, MD, MA, MT, NV, NH, PA, WA). Leave on if unsure."
          />
          <SettingsFieldRow
            title="Record all calls for quality and training"
            description="Recordings are encrypted and retained for 90 days by default."
            isDirty={dirty('recordAllCalls')}
          >
            <SettingsToggle value={state.recordAllCalls} onChange={(v) => upd('recordAllCalls', v)} />
          </SettingsFieldRow>
          <SettingsFieldRow
            title="Require two-party consent for recording"
            description="Elva announces the recording at call start and pauses for caller consent. Required by law in 11 states."
            isDirty={dirty('twoPartyConsent')}
          >
            <SettingsToggle value={state.twoPartyConsent} onChange={(v) => upd('twoPartyConsent', v)} />
          </SettingsFieldRow>
          <SettingsFieldRow
            title="Disclose AI nature on first interaction"
            description="On every patient's first call, Elva says 'I'm Elva, the AI receptionist at Bayside Dental.' Best practice everywhere."
            isDirty={dirty('discloseAINature')}
            last
          >
            <SettingsToggle value={state.discloseAINature} onChange={(v) => upd('discloseAINature', v)} />
          </SettingsFieldRow>
        </div>
      </section>
    </>
  );
}
